import React from "react";
import { Button, Menu } from "antd";
import { withTranslation } from "react-i18next";
import { FormatIconMenu } from "../../helpers/formatIconMenu";
import { withRouter } from "react-router-dom";
import AppContext from "../AppContext";
import { LoadingOutlined, ReloadOutlined } from "@ant-design/icons";
import { debounce } from "lodash";
import { formatCurrencyCompact } from "../../utils";
import { publicPage } from "../../route.page";

class Index extends React.Component {
  static contextType = AppContext;
  constructor(props) {
    super(props);
    this.state = {
      stateOpenKeys: [],
      selectedKeys: [],
      userRole: null,
      userPermissions: new Set(),
      isMobile: window.innerWidth <= 768,
    };
    this.onMenuItemClick = this.onMenuItemClick.bind(this);
    this.debouncedFetch = debounce(this.onMenuItemClick, 100);
  }

  componentDidMount() {
    this.props.i18n.on("languageChanged", this.handleLanguageChange);
    window.addEventListener("resize", this.handleResize);
    this.loadUserInfo();
  }

  componentDidUpdate(prevProps) {
    const { pathname } = this.props.location;
    const { resProfileAdmin, loadingMenu } = this.props;

    // 1. ตรวจสอบเมื่อ Path เปลี่ยน
    if (pathname !== prevProps.location.pathname) {
      this.updateSelectedKeys(pathname);
      this.checkAccessAndRedirect(pathname);
    }

    // 2. แก้ไข: ตรวจสอบเมื่อข้อมูล Profile หรือสถานะการโหลดเปลี่ยน
    // เพื่อให้เมนูแสดงผลทันทีที่ข้อมูลมาถึง
    if (
      (resProfileAdmin !== prevProps.resProfileAdmin ||
        loadingMenu !== prevProps.loadingMenu) &&
      resProfileAdmin &&
      !loadingMenu
    ) {
      this.loadUserInfo();
    }
  }

  componentWillUnmount() {
    this.props.i18n.off("languageChanged", this.handleLanguageChange);
    this.debouncedFetch.cancel();
    window.removeEventListener("resize", this.handleResize);
  }

  handleResize = () => {
    this.setState({ isMobile: window.innerWidth <= 768 });
  };

  checkAccessAndRedirect = (path) => {
    const { menuObject, resProfileAdmin, isPermissionAdmin, history } =
      this.props;

    // ตัด query string ออกเพื่อการเช็คที่แม่นยำ
    const cleanPath = path.split("?")[0];

    if (publicPage.includes(cleanPath)) return true;

    // จัดการกรณีพิเศษ finance-88
    if (resProfileAdmin?.username === "finance-88") {
      if (cleanPath === "/invoices") return true;
      if (cleanPath === "/dashboard") {
        history.push("/invoices");
        return true;
      }
      history.push("/page403");
      return false;
    }

    const currentMenuItem = this.findMenuItemByPath(menuObject, cleanPath);

    if (currentMenuItem) {
      const requiredAccess = Array.isArray(currentMenuItem.access)
        ? currentMenuItem.access
        : [currentMenuItem.access].filter(Boolean);

      const requiredPermission = Array.isArray(currentMenuItem.permission)
        ? currentMenuItem.permission
        : [currentMenuItem.permission].filter(Boolean);

      const allRequired = [...requiredAccess, ...requiredPermission];

      // ถ้าไม่มีการกำหนดสิทธิ์เลย (และไม่ใช่หน้าที่อนุญาตด้านบน) ให้เช็คตามนโยบาย
      if (allRequired.length === 0) return true;

      const hasPermission = isPermissionAdmin.some((p) =>
        allRequired.includes(p.name),
      );

      if (hasPermission) {
        return true;
      } else {
        history.push("/page403");
        return false;
      }
    } else {
      history.push("/page404");
      return false;
    }
  };

  findMenuItemByPath = (items, path) => {
    if (!items) return null;
    for (const item of items) {
      if (item.path === path) return item;
      if (item.children) {
        const found = this.findMenuItemByPath(item.children, path);
        if (found) return found;
      }
    }
    return null;
  };

  loadUserInfo = () => {
    const { roleObject, resProfileAdmin, location } = this.props;
    if (!resProfileAdmin) return;

    const userRoleDataFromApi = roleObject?.find((o) => o.id === resProfileAdmin?.roleID,);

    let permissionsFromApi = [];
    let roleName = resProfileAdmin?.role || null;

    if (userRoleDataFromApi && Array.isArray(userRoleDataFromApi.permission)) {
      permissionsFromApi = userRoleDataFromApi.permission.filter((item) => item.isPublic === 1 && item.name).map((item) => item.name);
    }

    this.setState(
      {
        userRole: roleName,
        userPermissions: new Set(permissionsFromApi),
      },
      () => {
        this.updateSelectedKeys(location.pathname);
        this.checkAccessAndRedirect(location.pathname);
      },
    );
  };

  hasAccess = (item) => {
    const { resProfileAdmin, isPermissionAdmin } = this.props;
    if (resProfileAdmin?.username === "finance-88") return item.key === "12";
    const itemAccessList = [
      ...(Array.isArray(item.access) ? item.access : [item.access]),
      ...(Array.isArray(item.permission) ? item.permission : [item.permission]),
    ].filter(Boolean);

    if (itemAccessList.length === 0) return true;
    return isPermissionAdmin.some((p) => itemAccessList.includes(p.name));
  };

  handleLanguageChange = (lng) => this.setState({ currentLang: lng });

  onOpenChange = (openKeys) => {
    const latestOpenKey = openKeys.find((key) => this.state.stateOpenKeys.indexOf(key) === -1,);
    this.setState({ stateOpenKeys: latestOpenKey ? [latestOpenKey] : openKeys, });
  };

  processMenuItems = (menuItems, t) => {
    if (!menuItems) return [];
    const {
      countCustomer, countVerifySlip, countTransApprove,
      countTransDeposit, countTransWithdraw, countVerifyCustomer
    } = this.props;

    const format = (val) => formatCurrencyCompact(val || 0);

    return menuItems.flatMap((item) => {
      let newItem = { ...item };

      if (newItem.children) {
        newItem.children = this.processMenuItems(newItem.children, t);
      }

      // 2. เช็ค Access
      if (!this.hasAccess(newItem) && (!newItem.children || newItem.children.length === 0)) {
        return [];
      }

      // 3. เตรียม "Badge Data" ส่งไปให้ createMenuItem
      // mapping ตาม path สำหรับเมนูลูก

      const pathBadgeMap = {
        "/bank-bank-customer": `(${format(countCustomer)})`,
        "/verify-customer": `(${format(countVerifyCustomer)})`,
        "/deposit-transaction": `(${format(countTransDeposit)})`,
        "/top-up-approval": `(${format(countTransApprove)})`,
      };

      // mapping ตาม labelKey สำหรับเมนูหลัก (Parent)
      const labelBadgeMap = {
        "Customer's": `(${format(countCustomer)} / ${format(countVerifyCustomer)})`,
        "deposit": `(${format(countTransDeposit)} / ${format(countTransApprove)})`,
        "withdraw": `(${format(countTransWithdraw)})`,
        "CheckTheSlip": `(${format(countVerifySlip)})`,
      };
      // ตรวจสอบว่าเป็น Parent Badge หรือไม่
      const parentBadge = labelBadgeMap[newItem.labelKey];
      const childBadge = pathBadgeMap[newItem.path];

      newItem.badgeValue = childBadge || parentBadge || null;
      // ส่ง flag ไปบอก createMenuItem ว่าให้ทำตัวหนาถ้าเป็น parentBadge
      newItem.isParentBadge = !!parentBadge && !childBadge;

      // เลือกใช้ badge จาก path หรือ labelKey
      newItem.badgeValue = pathBadgeMap[newItem.path] || labelBadgeMap[newItem.labelKey] || null;

      return [this.createMenuItem(newItem, t)];
    });
  };
  createMenuItem = (item, t) => {
    const { labelKey, badgeValue, isParentBadge, ...rest } = item;
    const newItem = { ...rest };

    if (!labelKey) {
      FormatIconMenu(newItem);
      return newItem;
    }
    const labelText = t(labelKey);
    if (badgeValue) {
      newItem.label = (
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          paddingRight: "10px",
        }}>
          <span >{labelText}</span>
          <span style={{ fontSize: isParentBadge ? "12px": "11px", opacity: 0.7, marginLeft: "8px",fontWeight: isParentBadge ? "700" : "400"  }}>
            {badgeValue}
          </span>
        </div>
      );
    } else {
      newItem.label = labelText;
    }

    FormatIconMenu(newItem);
    return newItem;
  };
  updateSelectedKeys = (pathname) => {
    const { menuPaths, menuObject, levelKeys } = this.props;
    let currentSelectedKey = null;
    let longestMatchPath = "";

    for (const key in menuPaths) {
      const path = menuPaths[key];
      if (
        path &&
        pathname.startsWith(path) &&
        path.length > longestMatchPath.length
      ) {
        longestMatchPath = path;
        currentSelectedKey = key;
      }
    }

    if (currentSelectedKey) {
      const openKeys = [];
      let currentKey = currentSelectedKey;
      while (levelKeys[currentKey] > 1) {
        const parent = this.findParentMenuItemByKey(menuObject, currentKey);
        if (parent) {
          openKeys.unshift(parent.key);
          currentKey = parent.key;
        } else break;
      }
      this.setState({
        selectedKeys: [currentSelectedKey],
        stateOpenKeys: openKeys,
      });
    }
  };

  findMenuItemByKey = (items, key) => {
    if (!items) return null;
    for (const item of items) {
      if (item.key === key) return item;
      if (item.children) {
        const found = this.findMenuItemByKey(item.children, key);
        if (found) return found;
      }
    }
    return null;
  };

  findParentMenuItemByKey = (items, childKey) => {
    if (!items) return null;
    for (const item of items) {
      if (item.children) {
        if (item.children.some((child) => child.key === childKey)) return item;
        const found = this.findParentMenuItemByKey(item.children, childKey);
        if (found) return found;
      }
    }
    return null;
  };

  onMenuItemClick = (e) => {
    const {
      toggleCollapsed,
      onLocleChang,
      history,
      location,
      menuObject,
      menuPaths,
    } = this.props;
    const clickedItem = this.findMenuItemByKey(menuObject, e.key);
    if (!clickedItem) return;

    const path = menuPaths[e.key];
    if (path && location.pathname === path) return;

    if (clickedItem.key === "10" && !clickedItem.path) {
      window.open(`https://doc.payeasy88-xtb.info`, "_blank");
      return;
    }
    const hasPermission = this.checkAccessAndRedirect(
      clickedItem.path || path || "",
    );
    if (!hasPermission) return;

    toggleCollapsed();
    if (onLocleChang) onLocleChang(clickedItem);



    if (clickedItem.onClick && !clickedItem.path) {
      clickedItem.onClick();
    } else if (path) {
      history.push(path);
    }
  };

  reload = () => {
    const { fetchPromiseData, setLoadingMenu } = this.props;
    if (fetchPromiseData) fetchPromiseData();
    if (setLoadingMenu) setLoadingMenu();
  };

  render() {
    const { stateOpenKeys, selectedKeys } = this.state;
    const { t, loadingMenu, menuObject } = this.props;
    const isReady = !loadingMenu;
    const items = this.processMenuItems(menuObject, t);
    if (!isReady) {
      return (
        <Menu
          mode="inline"
          style={{ width: 256, background: "#f4f7f6", padding: 20 }}
          items={[
            {
              key: "loading",
              label: (
                <>
                  <span style={{ color: "#ccc" }}>
                    <LoadingOutlined /> {t("Loading menu...")}
                  </span>
                </>
              ),
            },
          ]}
        />
      );
    } else if (isReady && items.length === 0) {
      return (
        <div style={{ padding: "20px", textAlign: "center" }}>
          <Button icon={<ReloadOutlined />} onClick={this.reload}>
            {t("Reload Menu")}
          </Button>
        </div>
      );
    }

    return (
      <Menu
        mode="inline"
        selectedKeys={selectedKeys}
        openKeys={stateOpenKeys}
        onOpenChange={this.onOpenChange}
        onClick={this.debouncedFetch}
        style={{ width: 256, background: "#f4f7f6" }}
        items={items}
      />
    );
  }
}

export default withRouter(withTranslation()(Index));
